import {
    Button,
    Grid,
    IconButton,
    IconButtonProps,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { IAcxButtonProps } from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { MinimalPlayer } from "./Controls/MinimalPlayer";
import Player from "./Controls/Player";
import SoundClipEditorStore, {
    DownloadAudioClipTask,
} from "./Stores/SoundClipEditorStore";

export const toggleZoomViewTooltip =
    "Toggle Zoom View - hold 'Shift' while clicking to create clips from the Zoom View";

interface SoundClipVisualizerProps {
    viewModel: SoundClipEditorStore;
    editorHeight?: number;
    fullControls?: boolean;
    enablePlaybackRate?: boolean;
    allowUndo?: boolean;
    minimize?: boolean;
    noMenu?: boolean;
    enableZoom?: boolean;
    isMontage?: boolean;
    textButton?: string | null;
    textButtonProps?: Partial<IAcxButtonProps>;
    iconButton?: React.ReactNode;
    iconButtonProps?: Partial<IconButtonProps>;
    onPressPlay?: () => void;
    onPressPause?: () => void;
}

const SoundClipVisualizer: React.FC<SoundClipVisualizerProps> = observer(
    (props) => {
        const theme = useTheme();
        const smallDownMatch = useMediaQuery(theme.breakpoints.down("md"));
        const largeUpMatch = useMediaQuery("(min-width:1450px)");
        const [shiftPressed, setShiftPressed] = React.useState<boolean>(false);

        useEffect(() => {
            if (props.viewModel.showZoomView) {
                props.viewModel.createSoloZoomview();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.viewModel.showZoomView]);

        useEffect(() => {
            if (props.viewModel.showZoomView) {
                props.viewModel.toggleZoomView();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.fullControls]);

        useEffect(() => {
            function handleKeyDown(event) {
                if (event.shiftKey) {
                    setShiftPressed(true);
                }
            }

            function handleKeyUp(event) {
                if (event.key === "Shift") {
                    setShiftPressed(false);
                }
            }

            window.addEventListener("keydown", handleKeyDown);
            window.addEventListener("keyup", handleKeyUp);

            return () => {
                window.removeEventListener("keydown", handleKeyDown);
                window.removeEventListener("keyup", handleKeyUp);
            };
        }, []);

        const containerHeight = props.fullControls ? "119px" : "84px";

        return (
            <>
                <Grid
                    container
                    direction={props.fullControls ? "column" : "row"}
                    wrap="nowrap"
                    alignItems="center"
                    justifyContent="flex-start"
                    style={
                        props.isMontage &&
                        !!props.viewModel.getTaskError(DownloadAudioClipTask)
                            ? { marginTop: "1rem" }
                            : {}
                    }
                >
                    {!props.fullControls && (
                        <MinimalPlayer
                            viewModel={props.viewModel}
                            enablePlaybackRate={props.enablePlaybackRate}
                            minimize={props.minimize}
                            enableZoom={props.enableZoom}
                            iconButton={props.iconButton}
                            iconButtonProps={props.iconButtonProps}
                        />
                    )}
                    <Grid
                        container
                        item
                        xs={
                            props.fullControls
                                ? 12
                                : props.isMontage
                                ? largeUpMatch
                                    ? 11
                                    : 10
                                : 12
                        }
                        // The waveform's canvas is sized in pixels, so as a flex
                        // item it would hold this box at the width it was last
                        // drawn at. With no content minimum the box follows its
                        // row, the container below shrinks with it, and the
                        // store's observer sees the new width and refits.
                        sx={{ minWidth: 0 }}
                    >
                        <div
                            id="waveform"
                            style={{ width: "100%", minWidth: 0 }}
                            ref={(el) => {
                                if (!el) {
                                    props.viewModel.setCanvasRect(null);
                                    return;
                                }

                                props.viewModel.setCanvasRect(
                                    el.getBoundingClientRect(),
                                );
                            }}
                        >
                            {(props.viewModel.audioLoadFailed ||
                                props.viewModel.peaksInitFailed) &&
                            !props.viewModel.isStreamingMode ? (
                                <div
                                    style={{
                                        width: "100%",
                                        backgroundColor:
                                            theme.palette.lightBlue.background,
                                        height: containerHeight,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 8,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Waveform failed to load.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                            props.viewModel.peaksInitFailed
                                                ? props.viewModel.retryPeaksInit()
                                                : props.viewModel.retryAudioLoad()
                                        }
                                    >
                                        Try Again
                                    </Button>
                                </div>
                            ) : props.viewModel.isStreamingMode ? (
                                <div
                                    style={{
                                        width: "100%",
                                        backgroundColor:
                                            theme.palette.lightBlue.background,
                                        height: containerHeight,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {props.viewModel.peaksInitFailed ||
                                        props.viewModel.audioLoadFailed
                                            ? "Waveform unavailable for this audio. Playback is still available."
                                            : "Waveform unavailable for this audio. This may occur with larger or complex files. Playback is still available."}
                                    </Typography>
                                </div>
                            ) : props.viewModel.showZoomView ? (
                                <div
                                    style={{
                                        width: "100%",
                                        backgroundColor:
                                            theme.palette.lightBlue.background,
                                        height: containerHeight,
                                        // A canvas waiting for its refit stays inside its box.
                                        overflow: "hidden",
                                        cursor: props.noMenu
                                            ? "crosshair"
                                            : shiftPressed
                                            ? "ew-resize"
                                            : "grab",
                                    }}
                                    id={"zoomview-container"}
                                    ref={props.viewModel.setZoomviewElement}
                                    onMouseMove={
                                        props.viewModel.trackMouseCoordinates
                                    }
                                />
                            ) : (
                                <div
                                    style={{
                                        width: "100%",
                                        backgroundColor:
                                            theme.palette.lightBlue.background,
                                        height: containerHeight,
                                        // A canvas waiting for its refit stays inside its box.
                                        overflow: "hidden",
                                        cursor: props.noMenu
                                            ? "crosshair"
                                            : "ew-resize",
                                    }}
                                    id={"overview-container"}
                                    ref={props.viewModel.setOverviewElement}
                                    onMouseMove={
                                        props.viewModel.trackMouseCoordinates
                                    }
                                />
                            )}

                            {Boolean(
                                props.viewModel.getTaskError(
                                    DownloadAudioClipTask,
                                ),
                            ) && (
                                <div
                                    style={{
                                        position: "absolute",
                                        right: "0%",
                                        top: "5%",
                                    }}
                                >
                                    {props.viewModel.getTaskLoading(
                                        DownloadAudioClipTask,
                                    ) ? (
                                        <Typography>Retrying ...</Typography>
                                    ) : (
                                        <Tooltip title={"Retry clip download"}>
                                            <IconButton
                                                onClick={() =>
                                                    props.viewModel
                                                        .getTaskError(
                                                            DownloadAudioClipTask,
                                                        )
                                                        ?.task?.()
                                                }
                                                size="large"
                                            >
                                                <SyncProblemIcon
                                                    fontSize={
                                                        smallDownMatch ||
                                                        props.minimize
                                                            ? "small"
                                                            : "large"
                                                    }
                                                    sx={{ color: "red.main" }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                        </div>
                        {Boolean(props.allowUndo) && (
                            <div
                                style={{
                                    position: "absolute",
                                    right: "3px",
                                    top: "0px",
                                }}
                            >
                                <IconButton size="large">
                                    <HistoryIcon />
                                </IconButton>
                            </div>
                        )}

                        <audio
                            key={props.viewModel.audioElementKey}
                            ref={props.viewModel.setAudioElement}
                            onPlay={props.viewModel.onPlayingEvent}
                            onPause={props.viewModel.onPausingEvent}
                        ></audio>
                    </Grid>

                    {props.fullControls && (
                        <Player
                            enablePlaybackRate
                            viewModel={props.viewModel}
                            enableZoom={props.enableZoom}
                            iconButton={props.iconButton}
                            iconButtonProps={props.iconButtonProps}
                            textButton={props.textButton}
                            textButtonProps={props.textButtonProps}
                            onPressPlay={props.onPressPlay}
                            onPressPause={props.onPressPause}
                        />
                    )}
                </Grid>
            </>
        );
    },
);

export default SoundClipVisualizer;
